import { z } from 'zod';
import {
  AGENT_ACTIVATION_KINDS,
  AGENT_ACTIVATION_SOURCES,
  AGENT_BINDING_STATUS,
  AGENT_HARNESS_RUN_STATUS,
  AGENT_PROFILE_KINDS,
  AGENT_PROFILE_SPECIALIZATIONS,
  AGENT_PROFILE_STATUS,
  AGENT_PROFILE_VERSION_STATUS,
  AGENT_RUN_STATUS,
  TOOL_INVOCATION_STATUS,
} from '../constants/enums';

export const agentOperationsVersionSchema = z.literal('v3.agent-operations.1');
export const agentOperationsHealthSeveritySchema = z.enum(['info', 'warning', 'blocking']);

export const agentOperationsProfileSchema = z
  .object({
    id: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1),
    kind: z.enum(AGENT_PROFILE_KINDS),
    specialization: z.enum(AGENT_PROFILE_SPECIALIZATIONS).nullable(),
    status: z.enum(AGENT_PROFILE_STATUS),
    current_version_id: z.string().nullable(),
    owner_actor_id: z.string().nullable(),
    home_context_id: z.string().nullable(),
  })
  .strict();

export const agentOperationsVersionRowSchema = z
  .object({
    id: z.string().min(1),
    profile_id: z.string().min(1),
    version: z.number().int().positive(),
    status: z.enum(AGENT_PROFILE_VERSION_STATUS),
    output_schema_json: z.string().min(1),
    run_policy_json: z.string().min(1),
    model_policy_json: z.string().min(1),
    autonomy_policy_json: z.string().min(1),
    trigger_policy_json: z.string().min(1),
    promoted_at: z.string().nullable(),
    retired_at: z.string().nullable(),
  })
  .strict();

export const agentOperationsBindingSchema = z
  .object({
    id: z.string().min(1),
    profile_id: z.string().min(1),
    profile_version_id: z.string().min(1),
    actor_id: z.string().min(1),
    context_id: z.string().min(1),
    role_assignment_id: z.string().nullable(),
    activation: z.array(
      z
        .object({
          kind: z.enum(AGENT_ACTIVATION_KINDS),
          source: z.enum(AGENT_ACTIVATION_SOURCES),
        })
        .strict(),
    ),
    manual_run_enabled: z.number().int().min(0).max(1),
    status: z.enum(AGENT_BINDING_STATUS),
    valid_from: z.string().nullable(),
    valid_to: z.string().nullable(),
  })
  .strict();

export const agentOperationsHarnessRunSchema = z
  .object({
    id: z.string().min(1),
    profile_id: z.string().min(1),
    profile_version_id: z.string().min(1),
    harness_name: z.string().min(1),
    status: z.enum(AGENT_HARNESS_RUN_STATUS),
    fixture_context_snapshot_url: z.string().nullable(),
    output_url: z.string().nullable(),
    failure_summary: z.string().nullable(),
    started_at: z.string().min(1),
    finished_at: z.string().nullable(),
  })
  .strict();

export const agentOperationsRunSchema = z
  .object({
    id: z.string().min(1),
    profile_id: z.string().nullable(),
    profile_version_id: z.string().nullable(),
    agent_binding_id: z.string().nullable(),
    actor_id: z.string().nullable(),
    context_id: z.string().nullable(),
    activation_kind: z.enum(AGENT_ACTIVATION_KINDS).nullable(),
    activation_source: z.enum(AGENT_ACTIVATION_SOURCES).nullable(),
    status: z.enum(AGENT_RUN_STATUS),
    output_validation_status: z.string().nullable(),
    produced_event_count: z.number().int().min(0),
    produced_event_request_count: z.number().int().min(0),
    produced_claim_count: z.number().int().min(0),
    produced_evidence_count: z.number().int().min(0),
    produced_artifact_count: z.number().int().min(0),
    produced_decision_request_count: z.number().int().min(0),
    produced_action_card_request_count: z.number().int().min(0),
    requires_human_review: z.number().int().min(0).max(1),
    created_at: z.string().min(1),
    started_at: z.string().nullable(),
    finished_at: z.string().nullable(),
  })
  .strict();

export const agentOperationsToolInvocationSchema = z
  .object({
    id: z.string().min(1),
    agent_run_id: z.string().min(1),
    actor_id: z.string().min(1),
    authorized_via_type: z.string().nullable(),
    authorized_via_id: z.string().nullable(),
    context_id: z.string().nullable(),
    tool_name: z.string().min(1),
    requested_action: z.string().min(1),
    approval_mode: z.string().min(1),
    credential_grant_id: z.string().nullable(),
    status: z.enum(TOOL_INVOCATION_STATUS),
    sanitized_input_url: z.string().nullable(),
    sanitized_output_url: z.string().nullable(),
    error: z.string().nullable(),
    started_at: z.string().nullable(),
    finished_at: z.string().nullable(),
  })
  .strict();

export const agentOperationsPacketSchema = z
  .object({
    packet_type: z.literal('agent_operations'),
    version: agentOperationsVersionSchema,
    generated_at: z.string().min(1),
    profile: agentOperationsProfileSchema,
    current_version: agentOperationsVersionRowSchema.nullable(),
    versions: z.array(agentOperationsVersionRowSchema),
    bindings: z.array(agentOperationsBindingSchema),
    harness_runs: z.array(agentOperationsHarnessRunSchema),
    recent_runs: z.array(agentOperationsRunSchema),
    tool_invocations: z.array(agentOperationsToolInvocationSchema),
    health_flags: z.array(
      z
        .object({
          severity: agentOperationsHealthSeveritySchema,
          code: z.string().min(1),
          message: z.string().min(1),
        })
        .strict(),
    ),
    summary: z
      .object({
        versions: z.number().int().min(0),
        promoted_versions: z.number().int().min(0),
        enabled_bindings: z.number().int().min(0),
        harness_passes: z.number().int().min(0),
        recent_runs: z.number().int().min(0),
        tool_invocations: z.number().int().min(0),
      })
      .strict(),
  })
  .strict()
  .superRefine((packet, ctx) => {
    if (packet.summary.versions !== packet.versions.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['summary', 'versions'], message: 'version count mismatch' });
    }
    if (packet.summary.enabled_bindings !== packet.bindings.filter((item) => item.status === 'enabled').length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['summary', 'enabled_bindings'], message: 'enabled binding count mismatch' });
    }
    if (packet.profile.status === 'active' && packet.current_version?.status !== 'promoted') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['current_version'], message: 'active profile requires promoted current version' });
    }
    for (const binding of packet.bindings) {
      if (binding.profile_id !== packet.profile.id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bindings'], message: 'binding profile mismatch' });
      }
      if (packet.profile.kind === 'worker' && binding.status === 'enabled' && !binding.role_assignment_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bindings'], message: 'enabled worker binding requires role assignment' });
      }
    }
  });

export type AgentOperationsPacket = z.infer<typeof agentOperationsPacketSchema>;
